import React from 'react';
import { DatagridConfigurable, DataTable, TextField, ReferenceField, useRecordContext, useGetIdentity, usePermissions, NumberField, BooleanField, BulkDeleteButton, WrapperField, InPlaceEditor, NumberInput, ReferenceInput, AutocompleteInput, TextInput, useDataTableDataContext, useTranslate } from 'react-admin';
import { TableFooter, TableRow, TableCell, Typography, Box } from '@mui/material';

import DateField from '../common/fields/DateField';
import RecordSplitButton from '../common/buttons/RecordSplitButton';
import TooltipTextField from '../common/fields/TootlipTextField';
import BulkInvoicedButton from './buttons/BulkInvoicedButton';
import ProjectField from 'components/projects/fields/ProjectField';

const inPlaceEditorProps = { variant: 'standard', size: 'small', margin: 'none', label: false, autoFocus: true, helperText: false };
const optionText = choice => choice?.name ? choice.name : '';

const SplitButton = (props) => {
    const record = useRecordContext();
    const { data: account } = useGetIdentity();
    const { permissions } = usePermissions();
    const hasPermission = permissions?.includes('is_admin') || (!record?.invoiced && record?.user_id === account?.id);

    if (!hasPermission) {
        return null;
    }

    return (
        <RecordSplitButton
            {...props}
            hasShow={false}
            hasEdit={hasPermission}
            hasDelete={hasPermission}
        />
    );
};

const TimeEntryBulkActionsButtons = () => (
    <>
        <BulkInvoicedButton />
        <BulkDeleteButton mutationMode='pessimistic' />
    </>
);

const CustomTextInPlaceEditor = ({ source, ...props }) => {
    const record = useRecordContext();
    const { data: account } = useGetIdentity();

    // Workaround for autoFocus with textarea
    const handleFocusAtEnd = (e) => {
        const temp_value = e.target.value;
        e.target.value = '';
        e.target.value = temp_value;
    };

    if (record?.user_id === account?.id) {
        return (
            <InPlaceEditor
                source={source}
                editor={<TextInput source={source} multiline {...inPlaceEditorProps} sx={{ width: '100%' }} onFocus={handleFocusAtEnd} />}
            />
        );
    }

    return (
        <TextField {...props} source={source} />
    );
};

const HoursInPlaceEditor = (props) => {
    const record = useRecordContext();
    const { data: account } = useGetIdentity();

    if (record?.user_id === account?.id) {
        return (
            <InPlaceEditor
                editor={
                    <NumberInput source="hours" step={0.25} min={0.25} {...inPlaceEditorProps} />
                }
            >
                <NumberField source="pretty_hours" />
            </InPlaceEditor>
        );
    }

    return (
        <NumberField {...props} source="pretty_hours" />
    );
};

const TypeInPlaceEditor = (props) => {
    const record = useRecordContext();
    const { data: account } = useGetIdentity();

    if (record?.user_id === account?.id) {
        return (
            <InPlaceEditor
                editor={
                    <ReferenceInput source="type_id" reference="time_entry_types" sort={{ field: 'name', order: 'ASC' }}>
                        <AutocompleteInput optionText={optionText} {...inPlaceEditorProps} TextFieldProps={{ autoFocus: true }} />
                    </ReferenceInput>
                }
            >
                <ReferenceField label="Type" source="type_id" reference="time_entry_types" allowEmpty link={false}>
                    <TextField source="name" />
                </ReferenceField>
            </InPlaceEditor>
        );
    }

    return (
        <ReferenceField {...props} label="Type" source="type_id" reference="time_entry_types" allowEmpty link={false}>
            <TextField source="name" />
        </ReferenceField>
    );
};

const TimeEntryRow = ({ children, ...props }) => (
    <DatagridConfigurable bulkActionButtons={<TimeEntryBulkActionsButtons />} rowClick={false} {...props}>
        <TextField source="id" />
        <DateField source="date" />
        <ReferenceField label="User" source="user_id" reference="users" allowEmpty link="show">
            <TextField source="trigram" />
        </ReferenceField>
        <ProjectField label="Project" source="project_id" />
        <CustomTextInPlaceEditor source="description" />
        <TypeInPlaceEditor source="type_id" />
        <HoursInPlaceEditor source="hours" />
        <BooleanField source="invoiced" />
        <TooltipTextField source="notes" />
        {children}
        <WrapperField source="actions" label="">
            <SplitButton />
        </WrapperField>
    </DatagridConfigurable>
);

const TimeEntryTableFoot = () => {
    const data = useDataTableDataContext();
    const translate = useTranslate();
    const totalHours = data.reduce((sum, record) => sum + (record.hours ? record.hours : 0), 0);

    if (totalHours > 0) {
        return (
            <TableFooter>
                <TableRow>
                    <TableCell variant="footer" align="right" colSpan="100%">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <Box>
                                {translate('Total hours')} <Typography variant="caption" sx={{ fontSize: '10px' }}>({data.length} {translate('items')})</Typography>
                            </Box>
                            <NumberField source="total" record={{ total: totalHours }} options={{ style: 'unit', unit: 'hour' }} />
                        </Box>
                    </TableCell>
                </TableRow>
            </TableFooter>
        );
    }

    return null;
};

const TimeEntryTableRow = ({ children, ...props }) => (
    <DataTable bulkActionButtons={<TimeEntryBulkActionsButtons />} rowClick={false} foot={TimeEntryTableFoot} {...props}>
        <DataTable.Col source="id" />
        <DataTable.Col source="date" field={DateField} />
        <DataTable.Col label="User" source="user_id">
            <ReferenceField label="User" source="user_id" reference="users" allowEmpty link="show">
                <TextField source="trigram" />
            </ReferenceField>
        </DataTable.Col>
        <DataTable.Col label="Project" source="project_id" field={ProjectField} />
        <DataTable.Col source="description">
            <CustomTextInPlaceEditor source="description" />
        </DataTable.Col>
        <DataTable.Col label="Type" source="type_id">
            <TypeInPlaceEditor source="type_id" />
        </DataTable.Col>
        <DataTable.Col label="Hours" align="right" source="hours">
            <HoursInPlaceEditor source="hours" />
        </DataTable.Col>
        <DataTable.Col source="invoiced" field={BooleanField} />
        <DataTable.Col source="notes" field={TooltipTextField} />
        {children}
        <DataTable.Col source="actions" label={false}>
            <SplitButton />
        </DataTable.Col>
    </DataTable>
);

export default TimeEntryRow;
export { TimeEntryTableRow };